/**
 * @lines98/client/components/rail-popover — компактная кнопка боковой панели +
 * аккуратный попап (нативный `<dialog>`).
 *
 * Назначение (docs/specs/board-compact-rail.md): когда боковая панель `--rail-w`
 * слишком узкая, чтобы показать обвязку в полном виде (измеряемое условие, НЕ
 * диапазон пикселей), её содержимое сворачивается в компактные кнопки — каждая
 * открывает попап с ПОЛНЫМ содержимым. Доска при этом НЕ уменьшается.
 *
 * Это СВОЙ попап (owner-directive 2026-05-29: «можешь плодить новый … ты красиво
 * сделай»), не `info-popover.ts`. Отличия от info-popover:
 *   · триггер = полноширинная кнопка панели (не 36px-кружок `?`);
 *   · тело — произвольный элемент (buildBody), не только текст → внутрь можно
 *     положить кнопки действий;
 *   · ВСЕ размеры — токенами (`--m`, `--btn-h`, `--board-w`, `--fs-*`), без
 *     вписанных px (owner-rule «только формулы»).
 *
 * Нативный `<dialog>` + `showModal()` даёт из коробки: top-layer (выше любого
 * z-index), `::backdrop` затемнитель, focus-trap, Esc-закрытие, role="dialog".
 * Закрытие: backdrop / Esc / `×`. Один попап за раз (локальный реестр).
 *
 * Содержимое тела строится КАЖДЫЙ раз при открытии (`buildBody()`), поэтому живые
 * данные (статистика Свободно/Ходы) всегда актуальны.
 */

import './rail-popover.css';

import { t } from '../lib/i18n/index.js';
import { createIconButton, ICON_CLOSE } from './icon-button.js';

export interface RailPopoverOptions {
  /** Контейнер, куда монтируется кнопка-триггер (последним ребёнком). */
  readonly triggerHost: HTMLElement;
  /** Иконка на кнопке-триггере (напр. '☰' / 'ⓘ'). */
  readonly icon: string;
  /** aria-label кнопки-триггера (напр. «Действия» / «Информация»). */
  readonly ariaLabel: string;
  /** Заголовок в шапке попапа. */
  readonly title: string;
  /** Строит тело попапа заново при каждом открытии (живые данные актуальны). */
  readonly buildBody: () => HTMLElement | DocumentFragment;
  /** Доп. класс на кнопку-триггер (для иконки-специфичных стилей). */
  readonly triggerClassName?: string;
  /**
   * Готовая кнопка-триггер вместо авто-создаваемой иконки. Когда экрану нужна
   * ПОЛНОЦЕННАЯ кнопка-лейбл (напр. «Войти» в ряду действий result), открывающая
   * тот же общий попап (dialog + backdrop + Esc + focus-trap) — передаём свою
   * кнопку. Тогда модуль НЕ создаёт иконку и НЕ удаляет переданную при unmount
   * (её жизненным циклом владеет экран); `icon`/`triggerClassName`/`triggerHost`
   * для этого случая игнорируются.
   */
  readonly trigger?: HTMLButtonElement;
}

export interface RailPopoverHandle {
  /** Кнопка-триггер (для управления видимостью извне). */
  readonly trigger: HTMLButtonElement;
  open(): void;
  close(): void;
  unmount(): void;
}

// Один открытый попап за раз — закрываем остальные при открытии нового.
const openRegistry = new Set<() => void>();
function closeAllOpen(except?: () => void): void {
  for (const fn of openRegistry) if (fn !== except) fn();
}

let idCounter = 0;

export function mountRailPopover(opts: RailPopoverOptions): RailPopoverHandle {
  idCounter += 1;
  const id = `rail-popover-${idCounter}`;

  // ---- Триггер ----
  // Свой (переданный экраном) лейбл-триггер ИЛИ авто-создаваемая иконка панели.
  const ownsTrigger = opts.trigger === undefined;
  const trigger = opts.trigger ?? document.createElement('button');
  if (ownsTrigger) {
    trigger.type = 'button';
    trigger.className = `rail-compact-btn${opts.triggerClassName ? ` ${opts.triggerClassName}` : ''}`;
    trigger.textContent = opts.icon;
    opts.triggerHost.appendChild(trigger);
  }
  trigger.setAttribute('aria-label', opts.ariaLabel);
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', id);

  // ---- Dialog ----
  const dialog = document.createElement('dialog');
  dialog.className = 'rail-popover-dialog';
  dialog.id = id;

  const header = document.createElement('header');
  header.className = 'rail-popover-header';

  const titleEl = document.createElement('h2');
  titleEl.className = 'rail-popover-title';
  titleEl.textContent = opts.title;
  header.appendChild(titleEl);

  // × — примитив icon-button (ghost, SVG-крестик, тактильные 44px). Раньше тут была
  // руколепная × шириной 0.7·--btn-h (≈31px, ниже тактильного минимума) — устранено.
  const closeBtn = createIconButton({
    glyph: ICON_CLOSE,
    label: t('game.infoPopover.close'),
    variant: 'ghost',
    className: 'rail-popover-close',
  }).element;
  header.appendChild(closeBtn);

  dialog.appendChild(header);

  const bodyEl = document.createElement('div');
  bodyEl.className = 'rail-popover-body';
  dialog.appendChild(bodyEl);

  document.body.appendChild(dialog);

  let unmounted = false;

  const closeFn = (): void => {
    if (unmounted || !dialog.open) return;
    dialog.classList.add('rail-popover-dialog--closing');
    requestAnimationFrame(() => {
      dialog.close();
      dialog.classList.remove('rail-popover-dialog--closing');
    });
    trigger.setAttribute('aria-expanded', 'false');
    openRegistry.delete(closeFn);
  };

  const openFn = (): void => {
    if (unmounted) return;
    closeAllOpen(closeFn);
    // Перестраиваем тело при каждом открытии → живые данные актуальны.
    bodyEl.replaceChildren(opts.buildBody());
    dialog.showModal();
    trigger.setAttribute('aria-expanded', 'true');
    openRegistry.add(closeFn);
  };

  const onTriggerClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    openFn();
  };
  trigger.addEventListener('click', onTriggerClick);

  const onCloseClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    closeFn();
  };
  closeBtn.addEventListener('click', onCloseClick);

  // Backdrop-клик: target === dialog только когда клик попал в ::backdrop/паддинг.
  const onDialogClick = (e: MouseEvent): void => {
    if (e.target === dialog) closeFn();
  };
  dialog.addEventListener('click', onDialogClick);

  // Esc → native `close` event: синхронизируем aria + реестр.
  const onDialogClose = (): void => {
    if (unmounted) return;
    trigger.setAttribute('aria-expanded', 'false');
    openRegistry.delete(closeFn);
  };
  dialog.addEventListener('close', onDialogClose);

  return {
    trigger,
    open: openFn,
    close: closeFn,
    unmount(): void {
      if (unmounted) return;
      unmounted = true;
      openRegistry.delete(closeFn);
      trigger.removeEventListener('click', onTriggerClick);
      closeBtn.removeEventListener('click', onCloseClick);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('close', onDialogClose);
      if (dialog.open) dialog.close();
      // Удаляем только САМИ созданную иконку; переданный экраном триггер живёт
      // в его DOM и удаляется экраном (мы лишь снимаем свой слушатель + ARIA).
      if (ownsTrigger && trigger.parentNode) trigger.parentNode.removeChild(trigger);
      else {
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-expanded');
        trigger.removeAttribute('aria-controls');
      }
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    },
  };
}
